/**
 * Auth types — kept aligned with zafroi-mvp-runtime's
 * src/modules/platform/auth/current-user.presenter.ts (FeCurrentUser) and
 * src/modules/platform/auth/auth.service.ts (LoginResponse / RefreshResponse).
 * If either changes, update here too.
 */

import type { RawWorkspaceApp } from '@zafroi/ui-kit';

export type ApiClientTier =
  | 'PLATFORM_ADMIN'
  | 'TENANT_ADMIN'
  | 'INTEGRATION'
  | 'PORTAL';

export interface CurrentUser {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  role: string;
  /** Real tenant-wide role names from /auth/me (Owner/Admin/Business Head/…). */
  roles?: string[];
  /** Effective permission keys from /auth/me — drives per-user product access. */
  permissions?: string[];
  /** Runtime workspace app registry (ADR-037) — what the AppSwitcher renders. */
  workspace?: { apps: RawWorkspaceApp[] };
  tier: ApiClientTier;
  tenantId: string;
  tenantCode: string;
  tenantName: string;
  tenantCountry?: string;
  tenantCurrency?: string;
  tenantFiscalYearStartMonth?: number;
  organizationId?: string;
  organizationName?: string;
  mfaEnrolled: boolean;
  lastLoginAt?: string;
  featureFlags?: string[];
  branding?: { primaryColor?: string; logoUrl?: string };
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: CurrentUser;
}

export interface MfaRequiredResponse {
  mfaRequired: true;
  mfaSessionToken: string;
  user: { fullName: string; email: string; mfaMethod: 'totp' };
}

export type LoginResult = LoginResponse | MfaRequiredResponse;

export function isMfaRequired(r: LoginResult): r is MfaRequiredResponse {
  return 'mfaRequired' in r && r.mfaRequired === true;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

export interface LoginPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface MfaVerifyPayload {
  mfaSessionToken: string;
  code: string;
}
